import { useAppDispatch, useAppSelector } from 'app/hooks';
import { VideoFunctionListItem } from 'features/VideoFunction/models';
import {
  selectVideoFunctionData,
  selectVideoFunctionDraggingItemId,
  selectVideoFunctionSelectedDroppableId,
  selectVideoFunctionSelectedItemIdMap,
  selectVideoFunctionSelectedItemIds,
  videoFunctionActions,
} from 'features/VideoFunction/videoFunctionSlice';
import React, { useContext } from 'react';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { PRIMARY_BUTTON } from '../../../../utils';
import { wasMultiSelectKeyUsed, wasToggleInSelectionGroupKeyUsed } from '../../utils/dragutils/utils';
import { COLUMN_WIDTH, VideoFunctionContext } from '../VideoFunctionListTable';

export interface VideoFunctionRowProps {
  rowData: VideoFunctionListItem;
  rowIndex: number;
}

function VideoFunctionRow({ rowData, rowIndex }: VideoFunctionRowProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // selector
  const data = useAppSelector(selectVideoFunctionData);
  const selectedDroppableId = useAppSelector(selectVideoFunctionSelectedDroppableId);
  const selectedItemIds = useAppSelector(selectVideoFunctionSelectedItemIds);
  const selectedItemIdMap = useAppSelector(selectVideoFunctionSelectedItemIdMap);
  const draggingItemId = useAppSelector(selectVideoFunctionDraggingItemId);

  const videoFunctionContext = useContext(VideoFunctionContext);
  const onRestoreVideoFunction = videoFunctionContext.onRestoreVideoFunction;
  const onSelectedRowChanged = videoFunctionContext.onSelectedRowChanged;

  const draggableId = `${rowData.id}`;
  const droppableId = `root`;

  const isSelected: boolean = droppableId === selectedDroppableId && Boolean(selectedItemIdMap[draggableId]);
  const isGhosting: boolean = isSelected && Boolean(draggingItemId) && draggingItemId !== draggableId;
  const selectionCount = selectedItemIds.length;

  function onKeyDown(
    event: React.KeyboardEvent<HTMLDivElement>,
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot
  ) {
    if (event.defaultPrevented) {
      return;
    }

    if (snapshot.isDragging) {
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    // using the event for selection
    event.preventDefault();

    performAction(event);
  }

  // Using onClick as it will be correctly
  // preventing if there was a drag
  function onClick(event: React.MouseEvent<HTMLDivElement>) {
    if (event.defaultPrevented) {
      return;
    }

    if (event.button !== PRIMARY_BUTTON) {
      return;
    }

    // marking the event as used
    const targetElement = event.target as HTMLElement;
    if (!targetElement.classList.contains('form-check-input')) {
      event.preventDefault();
    }

    // (event.currentTarget as HTMLDivElement).focus();
    performAction(event);
  }

  function onTouchEnd(event: React.TouchEvent<HTMLDivElement>) {
    if (event.defaultPrevented) {
      return;
    }

    // marking the event as used
    // we would also need to add some extra logic to prevent the click
    // if this element was an checkbox
    const targetElement = event.target as HTMLElement;
    if (!targetElement.classList.contains('form-check-input') && !targetElement.classList.contains('dan-btn-expand')) {
      event.preventDefault();
    }

    // props.toggleSelectionInGroup(props.task.id);
    dispatch(videoFunctionActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: draggableId }));
  }

  function performAction(event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) {
    // const { task, toggleSelection, toggleSelectionInGroup, multiSelectTo } = props;
    if (wasToggleInSelectionGroupKeyUsed(event)) {
      dispatch(videoFunctionActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: draggableId }));
      return;
    }

    if (wasMultiSelectKeyUsed(event)) {
      // multiSelectTo(task.id);
      dispatch(
        videoFunctionActions.multiSelectTo({
          arg: {
            data,
            selectedItemIds,
            droppableId: droppableId,
          },
          newItemId: draggableId,
        })
      );
      return;
    }

    dispatch(videoFunctionActions.toggleSelection({ selectedDroppableId: droppableId, itemId: draggableId }));
  }

  return (
    <Draggable draggableId={`${draggableId}`} index={rowIndex}>
      {(provided, snapshot) => {
        const shouldShowSelection: boolean = snapshot.isDragging && selectionCount > 1;
        const style = videoFunctionContext.getItemStyle(
          snapshot.isDragging,
          provided.draggableProps.style,
          isSelected,
          isGhosting
        );
        return (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{
              ...style,
            }}
            onClick={onClick}
            onTouchEnd={onTouchEnd}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => onKeyDown(event, provided, snapshot)}
          >
            <div className="dan-tr">
              <div
                className="dan-td d-flex justify-content-center flex-grow-0 flex-shrink-0"
                style={{
                  width: `${COLUMN_WIDTH[0]}%`,
                  backgroundColor: rowData.temp_delete_flg ? 'rgb(242, 242, 242)' : style.background,
                }}
              >
                {/* <Form.Check
                  value={`${rowIndex}`}
                  tabIndex={-1}
                  className="chk-ews-selection custom-checkbox"
                  onChange={videoFunctionContext.onSelectedRowChanged}
                ></Form.Check> */}
                {rowData.temp_delete_flg && (
                  <Button className="btn--orange" onClick={onRestoreVideoFunction?.bind(null, Number(rowData.id))}>
                    {t('videoFunctionListPage.restore')}
                  </Button>
                )}
                {!rowData.temp_delete_flg && (
                  <Form.Check
                    className="chk-videofunction-item custom-checkbox"
                    data-type="videofunction"
                    data-videofunctionname={rowData.name}
                    value={rowData.id}
                    onChange={onSelectedRowChanged}
                  ></Form.Check>
                )}
              </div>
              <div
                className="dan-td flex-grow-0 flex-shrink-0"
                style={{
                  width: `${COLUMN_WIDTH[1]}%`,
                  backgroundColor: rowData.temp_delete_flg ? 'rgb(242, 242, 242)' : style.background,
                }}
              >
                <span>{rowData.name}</span>
              </div>
              <div
                className="dan-td flex-grow-0 flex-shrink-0"
                style={{
                  width: `${COLUMN_WIDTH[2]}%`,
                  backgroundColor: rowData.temp_delete_flg ? 'rgb(242, 242, 242)' : style.background,
                }}
              >
                <span>{rowData.note}</span>
              </div>

              {shouldShowSelection ? <div className="selection-count">{selectionCount}</div> : null}
            </div>
          </div>
        );
      }}
    </Draggable>
  );
}

export default VideoFunctionRow;
